import { useEffect } from 'react';
import { create } from 'zustand';
import { CheckCircle2, XCircle, AlertTriangle, Info } from 'lucide-react';

type Severity = 'info' | 'success' | 'warning' | 'error';

interface Toast {
  id: number;
  title?: string;
  message: string;
  severity: Severity;
  persistent: boolean;
  hiding?: boolean;
}

interface ToastStore {
  toasts: Toast[];
  push: (input: Partial<Toast> & { message: string }) => void;
  dismiss: (id: number) => void;
}

let seq = 1;

export const useToast = create<ToastStore>((set) => ({
  toasts: [],
  push: (input) =>
    set((s) => {
      const persistent = input.persistent ?? input.severity === 'error';
      const t: Toast = {
        id: seq++,
        severity: 'info',
        persistent,
        ...input,
      };
      return { toasts: [...s.toasts, t].slice(-6) };
    }),
  dismiss: (id) =>
    set((s) => ({
      toasts: s.toasts.map((t) => (t.id === id ? { ...t, hiding: true } : t)),
    })),
}));

// Check sessionStorage for a pending toast (set across navigations)
function loadPersistedToast(): void {
  try {
    const raw = sessionStorage.getItem('sl.toast');
    if (!raw) return;
    sessionStorage.removeItem('sl.toast');
    const parsed = JSON.parse(raw) as Partial<Toast> & { message: string };
    useToast.getState().push(parsed);
  } catch { /* ignore */ }
}

/**
 * Push an error toast unless the error has already been handled elsewhere
 * (e.g. by handleTierError). Checks the `handled` marker so callers don't
 * need to know which error types were pre-toasted.
 */
export function pushError(err: unknown, fallback = 'Failed'): void {
  if (err && typeof err === 'object' && (err as { handled?: boolean }).handled) return;
  const msg = (err as { message?: string })?.message?.trim();
  // Raw error codes (no spaces) aren't user-friendly - show the fallback instead.
  const looksLikeCode = !!msg && !/\s/.test(msg);
  useToast.getState().push({ message: looksLikeCode ? fallback : msg || fallback, severity: 'error' });
}

export function persistToast(message: string, severity: Severity = 'info'): void {
  try {
    sessionStorage.setItem('sl.toast', JSON.stringify({ message, severity }));
  } catch { /* ignore */ }
}

const ICONS: Record<Severity, React.ReactElement> = {
  info: <Info size={18} />,
  success: <CheckCircle2 size={18} />,
  warning: <AlertTriangle size={18} />,
  error: <XCircle size={18} />,
};

export function ToastHost(): React.ReactElement {
  const { toasts, dismiss } = useToast();

  useEffect(() => {
    loadPersistedToast();
  }, []);

  useEffect(() => {
    const timers: number[] = [];
    for (const t of toasts) {
      if (t.hiding) {
        timers.push(
          window.setTimeout(() => {
            useToast.setState((s) => ({ toasts: s.toasts.filter((x) => x.id !== t.id) }));
          }, 300),
        );
        continue;
      }
      if (t.persistent) continue;
      timers.push(
        window.setTimeout(() => {
          dismiss(t.id);
        }, 5000),
      );
    }
    return (): void => {
      for (const h of timers) window.clearTimeout(h);
    };
  }, [toasts, dismiss]);

  return (
    <div className="sl-toast-host" aria-live="polite">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`sl-toast sl-toast-${t.severity}${t.persistent ? ' sl-toast-persistent' : ''}${t.hiding ? ' sl-hiding' : ''}`}
        >
          <div className="sl-toast-icon">{ICONS[t.severity]}</div>
          <div className="sl-toast-main">
            {t.title && <div className="sl-toast-title">{t.title}</div>}
            <div className="sl-toast-message">{t.message}</div>
          </div>
          <button type="button" className="sl-toast-close" onClick={() => dismiss(t.id)} aria-label="Close">
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
